/**
 * Composição do Extrato de Premiação a partir do ciclo 1S2026.
 *
 * A página monta os cards com uma escada de comparações de data, e a letra de cada card
 * ((A), (B), …) e o rótulo do Total ("(A + B + C + D)") eram literais soltos no JSX. Isso
 * funcionava enquanto todo mundo via os mesmos cards. Não funciona mais: no 1S2026 quem
 * tem Acordo de Trabalho Predefinido não vê "Premiação Pontos Realização Destaque" nem
 * "Premiação Coletiva", e passa a ver "Conversão da Faixa de Premiação em Espécie" — mas
 * o rótulo continuava dizendo "(A + B + C + D)", citando duas letras que não estão na
 * tela. Aqui a lista visível e o rótulo saem do MESMO array, então não há como divergirem.
 *
 * **Só vale de 1S2026 em diante.** Para ciclos anteriores devolve `novaComposicao: false`
 * e a página segue pelos ternários que já existiam — o extrato de um ciclo fechado é
 * documento que o funcionário já leu e conferiu, e mudar letra lá é risco sem contrapartida.
 *
 * A composição depende só de (ciclo, tipo de acordo), nunca dos valores: é isso que faz um
 * Predefinido com `pontos_faixa` igual a 0 continuar vendo o card com "0", em paridade com
 * o card de Realização Destaque, que já mostra 0 para híbrido sem entrega.
 */

import { getNamedDates } from 'services/utils';

export type CardExtrato = 'jogandoJunto' | 'realizacaoDestaque' | 'conversaoFaixa' | 'cultura' | 'coletiva';

export interface ContextoExtratoPremiacao {
  /** `date_start` do ciclo selecionado; `undefined` enquanto a lista de ciclos não carregou. */
  dateStart?: number;
  /**
   * `tipo_de_acordo` do jogador NO CICLO consultado — vem de `player_fields_<ciclo>__c`,
   * não da situação atual. Fica `undefined` quando o snapshot do ciclo não existe.
   */
  tipoDeAcordo?: string;
}

export interface ComposicaoExtrato {
  /** `false` para ciclos anteriores a 1S2026: a página usa o caminho legado. */
  novaComposicao: boolean;
  /** Cards visíveis, na ordem de renderização. Vazio no modo legado. */
  cards: CardExtrato[];
  /** Letra de cada card visível. Vazio no modo legado. */
  letras: Partial<Record<CardExtrato, string>>;
  /** Ex.: `(A + B + C)`. String vazia no modo legado. */
  rotuloTotal: string;
}

const ALFABETO = 'ABCDEFGHIJ'.split('');

const MODO_LEGADO: ComposicaoExtrato = { novaComposicao: false, cards: [], letras: {}, rotuloTotal: '' };

/**
 * Ordem de renderização e o público de cada card.
 *
 * `realizacaoDestaque` e `conversaoFaixa` são o mesmo papel — "o que você entregou" — para
 * públicos diferentes, e são mutuamente exclusivos também no dado: em `pontos_livelo_1S2026__c`
 * nenhum documento tem `meus_pontos_task` e `pontos_faixa` ao mesmo tempo.
 *
 * `coletiva` some para Predefinido, mas continua aparecendo quando o acordo é desconhecido —
 * esconder um card por falta de informação é pior do que mostrá-lo com 0.
 */
const ORDEM_DOS_CARDS: { card: CardExtrato; visivelPara: (tipoDeAcordo?: string) => boolean }[] = [
  { card: 'jogandoJunto', visivelPara: () => true },
  { card: 'realizacaoDestaque', visivelPara: tipoDeAcordo => tipoDeAcordo === 'HIBRIDO' },
  { card: 'conversaoFaixa', visivelPara: tipoDeAcordo => tipoDeAcordo === 'PREDEFINIDO' },
  { card: 'cultura', visivelPara: () => true },
  { card: 'coletiva', visivelPara: tipoDeAcordo => tipoDeAcordo !== 'PREDEFINIDO' },
];

export function resolveComposicaoExtrato({ dateStart, tipoDeAcordo }: ContextoExtratoPremiacao): ComposicaoExtrato {
  if (typeof dateStart !== 'number' || dateStart < getNamedDates().ciclo_1S2026) {
    return MODO_LEGADO;
  }

  const cards = ORDEM_DOS_CARDS.filter(({ visivelPara }) => visivelPara(tipoDeAcordo)).map(({ card }) => card);

  const letras: Partial<Record<CardExtrato, string>> = {};
  cards.forEach((card, posicao) => {
    letras[card] = ALFABETO[posicao];
  });

  return {
    novaComposicao: true,
    cards,
    letras,
    rotuloTotal: `(${cards.map(card => letras[card]).join(' + ')})`,
  };
}
